import React, { useCallback, useEffect, useRef, useState } from "react"

// Galerie éditoriale « façon Apple » — remplace les grilles de vignettes.
//
// Le modèle Apple (galeries produit apple.com) tient en quatre choix :
//   ① une seule image dominante à la fois, très grande, plein cadre ;
//   ② défilement horizontal AIMANTÉ (scroll-snap) plutôt qu'une grille ;
//   ③ des commandes discrètes (palettes rondes translucides) + un compteur ;
//   ④ un agrandissement immersif plein écran, fond noir, image seule.
// Tout le reste disparaît : bordures, ombres marquées, gouttières serrées.
//
// Réutilisable : toute page pilier peut l'appeler avec ses propres visuels.
const EditorialGallery = ({ items = [], locale = "fr", credit = "", eyebrow = "" }) => {
  const scrollerRef = useRef(null)
  const closeBtnRef = useRef(null)
  const lastFocusRef = useRef(null)
  const [active, setActive] = useState(0)
  const [lightbox, setLightbox] = useState(null) // index ou null
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const t = locale === "en"
    ? { prev: "Previous image", next: "Next image", open: "View full screen", close: "Close", of: "of", hint: "Scroll or drag to browse" }
    : { prev: "Image précédente", next: "Image suivante", open: "Voir en plein écran", close: "Fermer", of: "sur", hint: "Faites défiler pour parcourir" }

  // Version haute définition pour le plein écran (les vignettes restent légères)
  const fullSrc = src => (src.includes("w=") ? src.replace(/w=\d+/, "w=2000") : src)

  const measure = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const cards = Array.from(el.children)
    if (!cards.length) return
    const origin = cards[0].offsetLeft
    let best = 0
    let bestDist = Infinity
    cards.forEach((c, i) => {
      const d = Math.abs(c.offsetLeft - origin - el.scrollLeft)
      if (d < bestDist) { bestDist = d; best = i }
    })
    setActive(best)
    setAtStart(el.scrollLeft <= 4)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined
    let raf = 0
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; measure() }) }
    el.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    el.scrollLeft = 0
    measure()
    const settle = setTimeout(() => { el.scrollLeft = 0; measure() }, 400)
    return () => {
      el.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      clearTimeout(settle)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [measure])

  const page = dir => {
    const el = scrollerRef.current
    if (!el) return
    const card = el.children[0]
    const step = card ? card.offsetWidth + 18 : el.clientWidth * 0.8
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    el.scrollBy({ left: dir * step, behavior: reduced ? "auto" : "smooth" })
  }

  // ── Plein écran ────────────────────────────────────────────────────────
  const open = i => {
    if (typeof document !== "undefined") lastFocusRef.current = document.activeElement
    setLightbox(i)
  }
  const close = useCallback(() => {
    setLightbox(null)
    const prev = lastFocusRef.current
    if (prev && prev.focus) prev.focus()
  }, [])

  useEffect(() => {
    if (lightbox === null) return undefined
    const onKey = e => {
      if (e.key === "Escape") { e.preventDefault(); close() }
      else if (e.key === "ArrowRight") setLightbox(i => (i + 1) % items.length)
      else if (e.key === "ArrowLeft") setLightbox(i => (i - 1 + items.length) % items.length)
    }
    document.addEventListener("keydown", onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    if (closeBtnRef.current) closeBtnRef.current.focus()
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [lightbox, items.length, close])

  // Balayage tactile en plein écran
  const touchX = useRef(null)
  const onTouchStart = e => { touchX.current = e.changedTouches[0].clientX }
  const onTouchEnd = e => {
    if (touchX.current === null) return
    const dx = e.changedTouches[0].clientX - touchX.current
    touchX.current = null
    if (Math.abs(dx) < 45) return
    setLightbox(i => (dx < 0 ? (i + 1) % items.length : (i - 1 + items.length) % items.length))
  }

  if (!items.length) return null

  const paddle = (dir, disabled) => (
    <button
      type="button"
      onClick={() => page(dir)}
      disabled={disabled}
      aria-label={dir < 0 ? t.prev : t.next}
      style={{
        width: 44, height: 44, borderRadius: "50%", border: "none", flexShrink: 0,
        background: disabled ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.06)",
        color: disabled ? "rgba(0,0,0,0.2)" : "#1d1d1f",
        cursor: disabled ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        transition: "background 200ms, transform 200ms",
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = "rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "scale(1.06)" } }}
      onMouseLeave={e => { e.currentTarget.style.background = disabled ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "scale(1)" }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {dir < 0 ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
      </svg>
    </button>
  )

  return (
    <div className="eg-root">
      <style>{`
        .eg-scroller { display:flex; gap:18px; overflow-x:auto; scroll-snap-type:x mandatory; -webkit-overflow-scrolling:touch; scrollbar-width:none; padding:4px 0 6px; }
        .eg-scroller::-webkit-scrollbar { display:none; }
        .eg-card { flex:0 0 auto; scroll-snap-align:start; position:relative; border:0; padding:0; background:none; cursor:zoom-in;
                   width:clamp(268px, 74vw, 940px); border-radius:28px; overflow:hidden; }
        .eg-card img { display:block; width:100%; aspect-ratio:3/2; object-fit:cover; transition:transform 900ms cubic-bezier(0.22,1,0.36,1); }
        .eg-card:hover img { transform:scale(1.035); }
        .eg-card:focus-visible { outline:3px solid #0071e3; outline-offset:3px; }
        .eg-cap { position:absolute; left:0; right:0; bottom:0; padding:56px 26px 22px; text-align:left; color:#fff;
                  background:linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%); }
        .eg-cap-t { font-size:clamp(15px,1.7vw,19px); font-weight:600; letter-spacing:-0.02em; }
        .eg-zoom { position:absolute; top:16px; right:16px; width:34px; height:34px; border-radius:50%; background:rgba(0,0,0,0.32);
                   backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity 240ms; }
        .eg-card:hover .eg-zoom, .eg-card:focus-visible .eg-zoom { opacity:1; }
        .eg-bar { display:flex; align-items:center; gap:14px; margin-top:18px; }
        .eg-dots { display:flex; gap:7px; flex:1; }
        .eg-dot { width:6px; height:6px; border-radius:50%; background:rgba(0,0,0,0.16); transition:background 260ms, transform 260ms; }
        .eg-dot[data-on="1"] { background:#1d1d1f; transform:scale(1.5); }
        .eg-count { font-size:13px; color:#6e6e73; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .eg-lb { position:fixed; inset:0; z-index:10000; background:rgba(0,0,0,0.97); display:flex; align-items:center; justify-content:center; }
        .eg-lb img { max-width:94vw; max-height:84vh; object-fit:contain; border-radius:6px; }
        .eg-lb-btn { position:absolute; width:46px; height:46px; border-radius:50%; border:none; background:rgba(255,255,255,0.1);
                     backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 200ms; }
        .eg-lb-btn:hover { background:rgba(255,255,255,0.2); }
        @media (prefers-reduced-motion: reduce) { .eg-card img, .eg-dot { transition:none !important; } .eg-card:hover img { transform:none; } }
        @media (max-width:720px) { .eg-paddles { display:none !important; } .eg-cap { padding:44px 18px 16px; } }
      `}</style>

      {eyebrow ? (
        <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6e6e73", marginBottom: 14 }}>{eyebrow}</div>
      ) : null}

      <div className="eg-scroller" ref={scrollerRef}>
        {items.map((it, i) => (
          <button key={it.src} type="button" className="eg-card" onClick={() => open(i)} aria-label={`${t.open} — ${it.alt}`}>
            <img src={it.src} alt={it.alt} loading={i === 0 ? "eager" : "lazy"} decoding="async" />
            <span className="eg-zoom" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
            </span>
            {it.caption ? <span className="eg-cap"><span className="eg-cap-t">{it.caption}</span></span> : null}
          </button>
        ))}
      </div>

      <div className="eg-bar">
        <div className="eg-dots" aria-hidden="true">
          {items.map((it, i) => <span key={it.src} className="eg-dot" data-on={i === active ? "1" : "0"} />)}
        </div>
        <div className="eg-count">{active + 1} {t.of} {items.length}</div>
        <div className="eg-paddles" style={{ display: "flex", gap: 8 }}>
          {paddle(-1, atStart)}
          {paddle(1, atEnd)}
        </div>
      </div>

      {credit ? <p style={{ fontSize: 12.5, color: "#a1a1a6", marginTop: 12 }}>{credit}</p> : null}

      {lightbox !== null && (
        <div className="eg-lb" role="dialog" aria-modal="true" aria-label={items[lightbox].alt}
          onClick={e => { if (e.target === e.currentTarget) close() }}
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <img src={fullSrc(items[lightbox].src)} alt={items[lightbox].alt} />
          <button ref={closeBtnRef} type="button" className="eg-lb-btn" style={{ top: 22, right: 22 }} onClick={close} aria-label={t.close}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
          {items.length > 1 && (
            <>
              <button type="button" className="eg-lb-btn" style={{ left: 22 }} aria-label={t.prev}
                onClick={() => setLightbox(i => (i - 1 + items.length) % items.length)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              <button type="button" className="eg-lb-btn" style={{ right: 22 }} aria-label={t.next}
                onClick={() => setLightbox(i => (i + 1) % items.length)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </>
          )}
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 26, textAlign: "center", pointerEvents: "none" }}>
            {items[lightbox].caption ? (
              <div style={{ fontSize: 15, fontWeight: 500, color: "rgba(255,255,255,0.92)", letterSpacing: "-0.01em" }}>{items[lightbox].caption}</div>
            ) : null}
            <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
              {lightbox + 1} {t.of} {items.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditorialGallery
